// src/business/components/erp/StatusBadge.jsx

// Supplier-friendly label overrides — hides internal finance terminology
const SUPPLIER_LABEL_OVERRIDES = {
  PENDING_FINANCE:  "Under Review",
  FINANCE_APPROVED: "Approved",
  FINANCE_REJECTED: "Declined",
  SENT_TO_SUPPLIER: "PO Received",
  OPEN_FOR_QUOTES: "Open for Response",
  PARTIALLY_QUOTED: "Response In Progress",
  QUOTES_RECEIVED: "Quotes Closed",
  QUOTE_DEADLINE_MISSED: "Deadline Missed",
};

const PRESETS = {
  // Core procurement statuses
  DRAFT:             { label: "Draft",             cls: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300" },
  PENDING:           { label: "Pending",           cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  SUBMITTED:         { label: "Submitted",         cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  APPROVED:          { label: "Approved",          cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  UNDER_REVIEW:      { label: "Under Review",      cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  ACCEPTED:          { label: "Accepted",          cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  CANCELLED:         { label: "Cancelled",         cls: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" },
  OPEN:              { label: "Open",              cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  CLOSED:            { label: "Closed",            cls: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" },
  EXPIRED:           { label: "Expired",           cls: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" },

  // PR / RFQ lifecycle
  PARTIALLY_USED_FOR_RFQ: { label: "Partially Used for RFQ", cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200" },
  FULLY_USED_FOR_RFQ:     { label: "Fully Used for RFQ",     cls: "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950 dark:text-fuchsia-200" },
  PENDING_SEND:           { label: "Pending Send",           cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  SENT:                   { label: "Sent",                   cls: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200" },
  VIEWED:                 { label: "Viewed",                 cls: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200" },
  OPEN_FOR_QUOTES:        { label: "Open for Quotes",        cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  PARTIALLY_QUOTED:       { label: "Partially Quoted",       cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  QUOTES_RECEIVED:        { label: "Quotes Received",        cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  QUOTE_DEADLINE_MISSED:  { label: "Quote Deadline Missed",  cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  DEADLINE_MISSED:        { label: "Deadline Missed",        cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  QUOTED:                 { label: "Quoted",                 cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  AWARDED:                { label: "Awarded",                cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200" },
  CONVERTED_TO_PO:        { label: "Converted to PO",        cls: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200" },
  REJECTED:               { label: "Rejected",               cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },

  // PO statuses
  PENDING_FINANCE:   { label: "Pending Finance",   cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  FINANCE_APPROVED:  { label: "Finance Approved",  cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  FINANCE_REJECTED:  { label: "Finance Rejected",  cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  SENT_TO_SUPPLIER:  { label: "Sent to Supplier",  cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  ACKNOWLEDGED:      { label: "Acknowledged",      cls: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200" },
  SUPPLIER_DECLINED: { label: "Supplier Declined", cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  PARTIALLY_FULFILLED: { label: "Partially Fulfilled", cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200" },
  FULFILLED:         { label: "Fulfilled",         cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },

  // Delivery / Logistics
  PREPARED:          { label: "Prepared",         cls: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-200" },
  SHIPPED:           { label: "Shipped",           cls: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200" },
  DISPATCHED:        { label: "Dispatched",        cls: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200" },
  IN_TRANSIT:        { label: "In Transit",        cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
  DELIVERED:         { label: "Delivered",          cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },

  // Stock receipts
  PENDING_RECEIPT:   { label: "Pending Receipt",   cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  INSPECTING:        { label: "Inspecting",        cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  PARTIAL:           { label: "Partial",           cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  DISPUTED:          { label: "Disputed",          cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  PENDING_APPROVAL:  { label: "Pending Approval",  cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },
  PARTIALLY_RECEIVED:{ label: "Partially Received", cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200" },
  FULLY_RECEIVED:    { label: "Fully Received",    cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  RECEIVED_WITH_DISCREPANCY: { label: "Received with Discrepancy", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" },

  // Product statuses
  LISTED:            { label: "Listed",            cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  ARCHIVED:          { label: "Archived",          cls: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" },

  // Generic
  ACTIVE:            { label: "Active",            cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
  INACTIVE:          { label: "Inactive",          cls: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" },
  BLOCKED:           { label: "Blocked",           cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200" },
  DECLINED:          { label: "Declined",          cls: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" },
};

export default function StatusBadge({ status, label: customLabel, context, className = "" }) {
  const preset = PRESETS[status] || {
    label: status?.replace(/_/g, " ") || "Unknown",
    cls: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  };

  const displayLabel = customLabel
    || (context === "supplier" && SUPPLIER_LABEL_OVERRIDES[status])
    || preset.label;

  return (
    <span
      className={`inline-flex min-h-6 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${preset.cls} ${className}`}
    >
      {displayLabel}
    </span>
  );
}
